import { CSS } from '@dnd-kit/utilities'
import { useSortable } from '@dnd-kit/sortable'
import { MCPTool, ToolMetaData } from 'Types/Tasks.types'
import HolderOutlined from '@ant-design/icons/lib/icons/HolderOutlined'
import styles from '../../AgenticAnnotationTaskUI.module.less'
import Flex from 'antd/lib/flex'
import ToolFilled from '@ant-design/icons/lib/icons/ToolFilled'
import Collapse from 'antd/lib/collapse/Collapse'
import { CollapseIcon } from 'Components/common/Icons/CollapseIcon'
import { DeleteFilled } from '@ant-design/icons'
import { JSONObject } from 'Types/Common.types'
import { JSONSchemaForm } from 'Components/common/JSONSchemaForm/JSONSchemaForm'
import Button from 'antd/lib/button'
import { isEmptySchema } from 'Utils/schema.utils'
import isEmpty from 'lodash/isEmpty'
import customizeValidator from '@rjsf/validator-ajv8/lib/customizeValidator'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { ErrorSchema } from '@rjsf/utils'
import { WaveLoader } from 'Components/common/WaveLoader/WaveLoader'
import { clsx } from 'shared/utils/react.utils'
import MarkdownRenderer from 'Components/common/MarkdownRenderer/MarkdownRenderer'
import { useDialog } from 'Hooks/use-dialog'
import { ActionModal } from 'Components/modal/ActionModal/ActionModal'

type ToolItemProps = {
    toolItem: ToolMetaData
    mcpTools: MCPTool[]
    id: string
    onDeleteTool: (toolId: string) => void
    onChange: (toolId: string, value: JSONObject) => void
    onTestToolCall: (toolId: string) => void
    readOnly: boolean
    isLoading: boolean
}

export const ToolItem = (props: ToolItemProps) => {
    const { toolItem, id, mcpTools, onChange, onDeleteTool, onTestToolCall, readOnly, isLoading } =
        props
    const [validationErrors, setValidationErrors] = useState<ErrorSchema>({})
    const [deleteToolId, setDeleteToolId] = useState<string | undefined>(undefined)
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
    })
    const confirmDeleteModal = useDialog(false)

    const style = useMemo(
        () => ({
            transform: CSS.Transform.toString(transform ? { ...transform, scaleY: 1 } : null),
            zIndex: isDragging ? 5 : 'auto',
            transition,
            border: isDragging ? '1px solid rgba(5, 5, 5, 0.06)' : '0px',
        }),
        [transform, isDragging, transition]
    )

    const toolDetails = useMemo(() => {
        return (
            mcpTools.find(mcpTool => mcpTool.name === toolItem.tool_run_info.data?.value.name)
                ?.details ?? { inputSchema: {}, description: '' }
        )
    }, [mcpTools, toolItem.tool_run_info.data?.value.name])

    const validator = useMemo(() => {
        const ajvValidator = customizeValidator()
        ajvValidator.handleSchemaUpdate(toolDetails.inputSchema)

        return ajvValidator
    }, [toolDetails.inputSchema])

    const onChangeValue = useCallback(
        (value: JSONObject) => {
            onChange(toolItem.tool_run_info.value_id, value)
        },
        [onChange, toolItem.tool_run_info.value_id]
    )

    const onClickDeleteTool = useCallback(() => {
        confirmDeleteModal.show()
        setDeleteToolId(toolItem.tool_run_info.value_id)
    }, [confirmDeleteModal, toolItem.tool_run_info.value_id])

    const onConfirmDeleteTool = useCallback(() => {
        if (deleteToolId) {
            onDeleteTool(deleteToolId)
        }
        confirmDeleteModal.hide()
        setDeleteToolId(undefined)
    }, [onDeleteTool, deleteToolId, confirmDeleteModal])

    const validateFormData = useCallback(
        (formData: JSONObject) => {
            if (!isEmptySchema(toolDetails.inputSchema)) {
                const validationResult = validator.validateFormData(
                    formData,
                    toolDetails.inputSchema
                )

                if (validationResult.errors.length > 0) {
                    const errorSchema: ErrorSchema = {}
                    validationResult.errors.forEach(error => {
                        const errorMessage =
                            error.message === 'must be integer'
                                ? 'Please enter a valid integer.'
                                : 'Please fill out this field.'
                        const path = error.property ?? ''
                        const cleanPath = path.replace(/^\./, '')
                        if (cleanPath) {
                            if (!errorSchema[cleanPath]) {
                                errorSchema[cleanPath] = {}
                            }
                            if (!errorSchema[cleanPath].__errors) {
                                errorSchema[cleanPath].__errors = []
                            }
                            errorSchema[cleanPath].__errors!.push(errorMessage)
                        } else {
                            if (!errorSchema.__errors) {
                                errorSchema.__errors = []
                            }
                            errorSchema.__errors.push(errorMessage)
                        }
                    })
                    setValidationErrors(errorSchema)
                    return false
                }
            }

            setValidationErrors({})
            return true
        },
        [toolDetails.inputSchema, validator]
    )

    const handleTestToolCall = () => {
        const isValid = validateFormData(toolItem.tool_run_info.data?.value?.input_params ?? {})

        if (!isValid) {
            return
        }

        onTestToolCall(toolItem.tool_run_info.value_id)
    }

    useEffect(() => {
        validateFormData(toolItem.tool_run_info.data?.value?.input_params ?? {})
    }, [validateFormData, toolItem.tool_run_info.data?.value?.input_params])

    return (
        <Flex
            className={styles['item-container']}
            ref={setNodeRef}
            style={style}
            gap={10}
            align="flex-start"
        >
            {!readOnly && (
                <HolderOutlined className={styles['drag-icon']} {...listeners} {...attributes} />
            )}

            {confirmDeleteModal.isOpen && (
                <ActionModal
                    title="Delete Tool"
                    content="Are you sure you want to delete this tool?"
                    onCancel={confirmDeleteModal.hide}
                    onConfirm={onConfirmDeleteTool}
                    getVisibility={() => confirmDeleteModal.isOpen}
                />
            )}

            <div className={styles['tool-item-content']}>
                <Flex
                    gap={10}
                    justify="space-between"
                    align="center"
                    className={styles['tool-item-header']}
                >
                    <Flex
                        className={styles['tool-item-name']}
                        gap={5}
                        justify="flex-start"
                        align="center"
                    >
                        <ToolFilled className={styles['tool-item-name-icon']} />
                        {toolItem.tool_run_info.data?.value.name}
                    </Flex>
                    {!readOnly && (
                        <DeleteFilled
                            className={styles['tool-item-delete-icon']}
                            onClick={onClickDeleteTool}
                        />
                    )}
                </Flex>

                <Collapse
                    expandIcon={({ isActive }) => (
                        <CollapseIcon isActive={isActive} className={styles['collapse-icon']} />
                    )}
                    bordered={false}
                    expandIconPosition={'end'}
                    items={[
                        {
                            key: toolItem.tool_run_info.data?.value.name ?? '',
                            label: (
                                <span className={styles['collapse-label']}>Tool Description</span>
                            ),
                            className: clsx(
                                styles['collapse-item'],
                                styles['description-collapse-item']
                            ),
                            children: (
                                <div className={styles['collapse-content']}>
                                    <MarkdownRenderer children={toolDetails.description} />
                                </div>
                            ),
                        },
                    ]}
                />

                <Collapse
                    expandIcon={({ isActive }) => (
                        <CollapseIcon isActive={isActive} className={styles['collapse-icon']} />
                    )}
                    bordered={false}
                    expandIconPosition={'end'}
                    items={[
                        {
                            key: toolItem.tool_run_info.data?.value.name ?? '',
                            label: (
                                <span className={styles['collapse-label']}>Tool Input Schema</span>
                            ),
                            className: styles['collapse-item'],
                            children: (
                                <pre className={styles['collapse-content']}>
                                    {JSON.stringify(toolDetails.inputSchema, null, 4)}
                                </pre>
                            ),
                        },
                    ]}
                />

                <Collapse
                    expandIcon={({ isActive }) => (
                        <CollapseIcon isActive={isActive} className={styles['collapse-icon']} />
                    )}
                    bordered={false}
                    expandIconPosition={'end'}
                    defaultActiveKey={['value-parameters']}
                    items={[
                        {
                            key: 'value-parameters',
                            label: <span className={styles['collapse-label']}>Tool Input</span>,
                            className: clsx(
                                styles['collapse-item'],
                                styles['input-parameters-collapse-item']
                            ),
                            children: (
                                <div className={styles['collapse-content']}>
                                    <JSONSchemaForm
                                        schema={toolDetails.inputSchema}
                                        value={
                                            toolItem.tool_run_info.data?.value?.input_params ?? {}
                                        }
                                        onChange={onChangeValue}
                                        readOnly={readOnly}
                                        extraErrors={validationErrors}
                                    />
                                </div>
                            ),
                        },
                    ]}
                />

                <Button
                    type="primary"
                    onClick={handleTestToolCall}
                    disabled={readOnly || Object.keys(validationErrors).length > 0}
                    className={styles['test-tool-call-button']}
                >
                    Fetch Tool Output
                </Button>

                {(!isEmpty(toolItem.tool_run_info.data?.value?.output) || isLoading) && (
                    <Collapse
                        expandIcon={({ isActive }) => (
                            <CollapseIcon isActive={isActive} className={styles['collapse-icon']} />
                        )}
                        bordered={false}
                        expandIconPosition={'end'}
                        items={[
                            {
                                key: toolItem.tool_run_info.data?.value.name ?? '',
                                label: (
                                    <span
                                        className={clsx(
                                            styles['collapse-label'],
                                            styles['test-tool-result-label']
                                        )}
                                    >
                                        Tool Output
                                    </span>
                                ),
                                className: clsx(
                                    styles['collapse-item'],
                                    styles['test-tool-result-collapse-item']
                                ),
                                children: (
                                    <pre className={styles['collapse-content']}>
                                        {isLoading ? (
                                            <Flex justify="center" align="center">
                                                <WaveLoader />
                                            </Flex>
                                        ) : (
                                            JSON.stringify(
                                                toolItem.tool_run_info.data?.value?.output ?? {},
                                                null,
                                                4
                                            )
                                        )}
                                    </pre>
                                ),
                            },
                        ]}
                    />
                )}
            </div>
        </Flex>
    )
}
